import { useVirtualizer } from '@tanstack/react-virtual';
import { useLayoutEffect, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Checkbox, Skeleton } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import type {
  InfinitePaginatedData,
  TableDataFileHeader,
  TableDataFileValidatedRow,
  ValidationSearchParams,
} from '../../api/types.js';
import {
  DEFAULT_HEADER_CELLS_COUNT,
  FIRST_CELL_WIDTH,
  ROW_HEIGHT,
} from '../../constants/tablesConfig.js';
import {
  useHasSufficientViewportHeight,
  useInfiniteScroll,
} from '../../hooks/index.js';
import {
  useChannelsCatalog,
  useEditTableDataFileField,
  usePlatformsCatalog,
} from '../../queries/index.js';
import { TableHeadCellSkeleton } from '../atoms/TableHeadCellSkeleton.jsx';
import { InfinitelyPaginatedTable } from '../molecules/InfinitelyPaginatedTable.jsx';
import { TableBodyValidatedCell } from '../molecules/TableBodyValidatedCell.jsx';
import {
  TableBody,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '../molecules/TableComponents.jsx';
import { ValidatedRow } from '../molecules/ValidatedRow.jsx';

type DataTableProps = {
  dataFileId: string;
  searchParams: ValidationSearchParams;
  data: InfinitePaginatedData<unknown>;
  headers: TableDataFileHeader[];
  rows: TableDataFileValidatedRow[];
  selection: {
    isInverted: boolean;
    selectedRows: Record<string, boolean>;
  };
  selectedRowsIds: string[];
  handleSelectAllRows: () => void;
  handleSelectRow: (
    rowID: string,
    rowIndex: number,
  ) => (checked: boolean) => void;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  hasNextPage?: boolean;
  fetchNextPage: () => Promise<unknown>;
};

export const DataTable = ({
  dataFileId,
  searchParams,
  data,
  headers,
  rows,
  selection,
  selectedRowsIds,
  handleSelectAllRows,
  handleSelectRow,
  isFetchingNextPage,
  isLoading,
  hasNextPage,
  fetchNextPage,
}: DataTableProps) => {
  const { mutate: editField } = useEditTableDataFileField(
    dataFileId,
    searchParams,
  );

  const { t } = useTranslation('@myn/import-hub');

  /*  Start Virtualization  */
  const threshold = (searchParams.itemsPerPage / 2) * ROW_HEIGHT;

  const scrollableContainer = useInfiniteScroll({
    hasNextPage,
    fetchNextPage,
    threshold,
  });

  // TanStack Virtual returns non-memoizable functions; React Compiler safely skips this component.
  /* oxlint-disable react-hooks-js/incompatible-library */
  // eslint-disable-next-line react-hooks/incompatible-library
  const rowVirtualizer = useVirtualizer({
    getScrollElement: () => scrollableContainer,
    count: rows.length,
    estimateSize: () => ROW_HEIGHT + 1,
    overscan: searchParams.itemsPerPage,
  });
  /* oxlint-enable react-hooks-js/incompatible-library */

  const virtualRows = rowVirtualizer.getVirtualItems();

  const paddingTop =
    virtualRows.length > 0 ? (virtualRows?.[0]?.start ?? 0) : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? rowVirtualizer.getTotalSize() - (virtualRows.at(-1)?.end ?? 0)
      : 0;

  /*  -------------------  */

  /* Fix HTML table layout */
  const [firstCellRef, setFirstCellRef] = useState<HTMLTableCellElement | null>(
    null,
  );
  const [isFirstCellOverflow, setIsFirstCellOverflow] = useState<
    boolean | null
  >(null);

  useLayoutEffect(() => {
    if (!isLoading && firstCellRef && isFirstCellOverflow === null) {
      setIsFirstCellOverflow(firstCellRef.clientWidth > FIRST_CELL_WIDTH);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, firstCellRef]);
  /*  -------------------  */

  const {
    data: { platformsByName },
  } = usePlatformsCatalog();
  const {
    data: { channelsByName },
  } = useChannelsCatalog();

  const handleEditField =
    (rowId: string, rowIndex: number, colIndex: number) => (rawValue: string) =>
      editField({
        rowId: rowId,
        payload: {
          columnPosition: headers[colIndex].columnPosition,
          rawValue,
        },
        meta: {
          rowIndex,
          colIndex,
        },
      });

  const isSelectionIndeterminate = Boolean(selectedRowsIds.length);

  const platformColumnIndex = headers.findIndex(
    (header) => header.formatType === 'platform',
  );

  const hasSufficientViewportHeight = useHasSufficientViewportHeight();
  const columnsCount = isLoading
    ? DEFAULT_HEADER_CELLS_COUNT + 1
    : headers.length + 1;

  return (
    <InfinitelyPaginatedTable
      columnsCount={columnsCount}
      data={data}
      isLoading={isLoading}
      isFetchingNextPage={isFetchingNextPage}
      placeholderRowsCount={50}
      stickyHeader
      className="table-fixed"
      tableContainerProps={{
        id: 'data-table-container',
        className: cn(
          'min-h-0',
          hasSufficientViewportHeight ? 'shrink' : 'shrink-0',
        ),
      }}
    >
      <TableHead>
        <TableRow>
          <TableHeaderCell
            ref={setFirstCellRef}
            className="w-[52px]"
            skeletonVariant="checkbox"
          >
            {isLoading ? (
              <Skeleton variant="rounded" className="h-3.5 w-3.5" />
            ) : (
              <Checkbox
                checked={
                  isSelectionIndeterminate
                    ? 'indeterminate'
                    : selection.isInverted
                }
                onCheckedChange={handleSelectAllRows}
                aria-label={t('Select all rows')}
                className="cursor-pointer"
              />
            )}
          </TableHeaderCell>
          {isLoading
            ? Array.from(
                { length: DEFAULT_HEADER_CELLS_COUNT },
                (_, index) => `header-skeleton-${index}`,
              ).map((skeletonId) => <TableHeadCellSkeleton key={skeletonId} />)
            : headers.map((col, index) => {
                const columnWidth =
                  index === platformColumnIndex ? '270px' : '150px';

                return (
                  <TableHeaderCell
                    key={`${col.headerName}-${col.columnPosition}`}
                    className={cn(
                      'break-words',
                      !isFirstCellOverflow &&
                        (columnWidth === '270px' ? 'w-[270px]' : 'w-[150px]'),
                    )}
                  >
                    {col.label}
                  </TableHeaderCell>
                );
              })}
        </TableRow>
      </TableHead>
      <TableBody>
        {paddingTop > 0 && (
          <tr>
            <td style={{ height: `${paddingTop}px` }} />
          </tr>
        )}
        {virtualRows.map((virtualRow) => {
          const rowIndex = virtualRow.index;
          const row = rows[rowIndex];
          const platformCell = row.validatedCells[platformColumnIndex];
          return (
            <ValidatedRow
              key={row.id}
              row={row}
              isSelected={
                selection.isInverted
                  ? selection.selectedRows[row.id] !== false
                  : selection.selectedRows[row.id]
              }
              handleSelectRow={handleSelectRow(row.id, rowIndex)}
            >
              {({ cell, colIndex }) => {
                const header = headers[colIndex];
                const isValid = !cell.error && !cell.isEdited;

                return (
                  <TableBodyValidatedCell
                    key={`${header.headerName}-${header.columnPosition}`}
                    formatType={header.formatType}
                    cell={cell}
                    platformCell={platformCell}
                    platformsByName={platformsByName}
                    channelsByName={channelsByName}
                    isDisabled={isValid || cell.isApiManaged}
                    handleEditField={handleEditField(
                      row.id,
                      rowIndex,
                      colIndex,
                    )}
                  />
                );
              }}
            </ValidatedRow>
          );
        })}
        {paddingBottom > 0 && (
          <tr>
            <td style={{ height: `${paddingBottom}px` }} />
          </tr>
        )}
      </TableBody>
    </InfinitelyPaginatedTable>
  );
};
